import apiClient from './client';

export interface ReportFilters {
  categoryId?: string;
  branchId?: string;
  personnelId?: string;
  departmentId?: string;
  companyId?: string;
  brandId?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}

export interface DeviceReportItem {
  id: string;
  serialNumber: string;
  productName: string;
  category?: string;
  brand: string;
  model: string;
  status: string;
  condition: string;
  branch?: string;
  location?: string;
  locationType?: string;
  assignedDate?: string;
  purchaseDate?: string;
  warrantyEndDate?: string;
}

export interface CategoryReportData {
  categoryId: string;
  categoryName: string;
  totalDevices: number;
  available: number;
  inUse: number;
  underRepair: number;
  scrapped: number;
  devices: DeviceReportItem[];
}

export interface CategoryWithinBranch {
  categoryId: string;
  categoryName: string;
  totalDevices: number;
  available: number;
  inUse: number;
  underRepair: number;
  scrapped: number;
  devices: DeviceReportItem[];
}

export interface BranchReportData {
  branchId: string;
  branchName: string;
  totalDevices: number;
  available: number;
  inUse: number;
  underRepair: number;
  scrapped: number;
  categories: CategoryWithinBranch[];
}

export interface PersonnelReportData {
  personnelId: string;
  personnelName: string;
  email: string;
  phone: string;
  department: string;
  branch: string;
  totalDevices: number;
  categories: CategoryWithinPersonnel[];
}

export interface CategoryWithinPersonnel {
  categoryId: string;
  categoryName: string;
  totalDevices: number;
  devices: DeviceReportItem[];
}

export interface CompanyReportData {
  companyId: string;
  companyName: string;
  totalDevices: number;
  available: number;
  inUse: number;
  underRepair: number;
  scrapped: number;
  devices: DeviceReportItem[];
}

export interface ReportResponse<T> {
  reportType: string;
  generatedAt: string;
  filters: ReportFilters;
  summary: {
    totalCategories?: number;
    totalBranches?: number;
    totalPersonnel?: number;
    totalCompanies?: number;
    totalDevices: number;
  };
  data: T[];
}

export interface SummaryReport {
  totalDevices: number;
  available: number;
  inUse: number;
  underRepair: number;
  scrapped: number;
  byCondition: {
    new: number;
    good: number;
    fair: number;
    poor: number;
  };
}

export const reportsApi = {
  getCategoryReport: async (filters: ReportFilters): Promise<ReportResponse<CategoryReportData>> => {
    const response = await apiClient.get('/reports/category', { params: filters });
    return response.data;
  },

  getBranchReport: async (filters: ReportFilters): Promise<ReportResponse<BranchReportData>> => {
    const response = await apiClient.get('/reports/branch', { params: filters });
    return response.data;
  },

  getPersonnelReport: async (filters: ReportFilters): Promise<ReportResponse<PersonnelReportData>> => {
    const response = await apiClient.get('/reports/personnel', { params: filters });
    return response.data;
  },

  getCompanyReport: async (filters: ReportFilters): Promise<ReportResponse<CompanyReportData>> => {
    const response = await apiClient.get('/reports/company', { params: filters });
    return response.data;
  },

  getSummary: async (branchId?: string): Promise<SummaryReport> => {
    const response = await apiClient.get('/reports/summary', { params: { branchId } });
    return response.data;
  },
};
